/**
 * Notification Service
 * 
 * Creates notifications explicitly in the app layer instead of relying on database triggers.
 * This gives us:
 * - Better error handling
 * - Clearer code flow
 * - Easier debugging
 * - No silent failures
 */

import { supabase } from "./supabaseClient";
import { logger } from "./logger";
import type { NotificationType } from "../types/orderbook";

interface CreateNotificationParams {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  listId?: string;
  itemId?: string;
  actorId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Creates a notification for a user
 * Returns success/failure - never throws
 */
export async function createNotification(params: CreateNotificationParams): Promise<boolean> {
  try {
    const { error } = await supabase.from("notifications").insert({
      user_id: params.userId,
      type: params.type,
      title: params.title,
      message: params.message,
      list_id: params.listId || null,
      item_id: params.itemId || null,
      actor_id: params.actorId || null,
      metadata: params.metadata || {},
      is_read: false,
    });

    if (error) {
      logger.error("Failed to create notification:", error);
      return false;
    }

    return true;
  } catch (err) {
    logger.error("Exception creating notification:", err);
    return false;
  }
}

/**
 * Notifies all collaborators of a list (except the actor)
 */
export async function notifyListCollaborators(params: {
  listId: string;
  actorId: string;
  type: NotificationType;
  title: string;
  message: string;
  itemId?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  try {
    // Get all collaborators except the actor
    const { data: collaborators, error } = await supabase
      .from("list_collaborators")
      .select("user_id")
      .eq("list_id", params.listId)
      .neq("user_id", params.actorId);

    if (error) {
      logger.error("Failed to get collaborators:", error);
      return;
    }

    // Create notification for each collaborator
    const notifications = (collaborators || []).map((collab) => ({
      user_id: collab.user_id,
      type: params.type,
      title: params.title,
      message: params.message,
      list_id: params.listId,
      item_id: params.itemId || null,
      actor_id: params.actorId,
      metadata: params.metadata || {},
      is_read: false,
    }));

    if (notifications.length === 0) {
      return;
    }

    // Insert all notifications at once
    const { error: insertError } = await supabase.from("notifications").insert(notifications);

    if (insertError) {
      logger.error("Failed to create notifications for collaborators:", insertError);
    }
  } catch (err) {
    logger.error("Exception notifying collaborators:", err);
  }
}

/**
 * Creates invitation notification
 */
export async function notifyInvitation(params: {
  inviteeEmail: string;
  inviterId: string;
  listId: string;
  invitationToken: string;
  role: string;
}): Promise<void> {
  try {
    // Try to find user by email
    const { data: invitee } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", params.inviteeEmail.toLowerCase())
      .single();

    if (!invitee) {
      // User doesn't exist yet - notification will be created when they sign up
      return;
    }

    await createNotification({
      userId: invitee.id,
      type: "list_invitation",
      title: "New list invitation",
      message: "You were invited to collaborate on a list",
      listId: params.listId,
      actorId: params.inviterId,
      metadata: {
        invitation_id: params.invitationToken,
        role: params.role,
      },
    });
  } catch (err) {
    logger.error("Exception creating invitation notification:", err);
  }
}
